import { useEffect, useMemo, useState } from 'react';
import { AchievementType, ProfileContextStateModel } from '~/apps/smartsend/lib/context/ProfileContext';
import Profile from './screens/profile';
import AccountsScreen from './screens/accounts';
import RulesScreen from './screens/rules';
import { Customer } from '~/lib/api/smartsend/types';
import IntroScreen from './screens/intro';
import CompleteScreen from './screens/complete';
import { OnboardingScreenWrapper } from './OnboardingScreenWrapper';
import { onlyUnique } from '~/lib/utils/array';
import { SetupProgress } from './SetupProgress';

type props = {
  isXs: boolean;
  loading: boolean;
  sendNow?: () => void;
  data: ProfileContextStateModel;
  justLinked?: string;
  justFailed?: string;

  markAsDone?: (
    keys: AchievementType[],
    outstandingActionsToAdd: string[],
    outstandingActionsToRemove: string[],
    meta: { [key: string]: any }
  ) => void;
  markAsTodo: (actions: string[]) => void;
};

const stages = ['WELCOME', 'CREATEACCOUNT', 'SETDEFAULTS', 'CONNECTSTORE', 'SENDPARCEL'];

const SmartSendSetup = ({ justFailed, justLinked, isXs, loading, data, markAsDone, sendNow, markAsTodo }: props) => {
  const [startedSetup, setSetupStarted] = useState(false);
  const [justFailedAcknowledged, setJustFailedAcknowledged] = useState(false);
  const [justLinkedAcknowledged, setJustLinkedAcknowledged] = useState(false);

  const done = useMemo(
    () => (data?.achievements.filter((x) => x.value).map((x) => x.key) ?? []).filter(onlyUnique),
    [data]
  );

  const [customer, setCustomer] = useState<Customer | null>();

  const screenToShow = useMemo(() => {
    // If they just linked an account, go to complete screen
    if (justLinked && !justLinkedAcknowledged) {
      return 'COMPLETE';
      // There is a component for acknowledging this but we've combined that into the complete page for simplicity.
      // return 'ACCOUNTS';
    }

    // Failed to link an account
    if (justFailed && !justFailedAcknowledged) {
      return 'ACCOUNTS';
    }

    if ((loading || !startedSetup) && !justLinked && !justFailed) {
      return 'INTRO';
    }

    // Only show if they haven't added a profile yet
    if (!done.includes('SmartSendActionCollectionAddressAdded')) {
      return 'PROFILE';
    }

    // Only show if they haven't seen the rule screen yet, or if they have just linked an account
    if (!done.includes('SmartSendSetupRuleScreen')) {
      return 'RULES';
    }

    // This one is specifically the screen as they can add more if they want.
    if (!done.includes('SmartSendSetupAccountScreen')) {
      return 'ACCOUNTS';
    }

    return 'COMPLETE';
  }, [startedSetup, justLinked, done, loading, justFailed, justFailedAcknowledged, justLinkedAcknowledged]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screenToShow]);

  const Progress = useMemo(() => {
    const cs = (() => {
      switch (screenToShow) {
        case 'INTRO':
        case 'PROFILE':
          return 'CREATEACCOUNT';
        case 'RULES':
          return 'SETDEFAULTS';
        case 'ACCOUNTS':
          return 'CONNECTSTORE';
        case 'COMPLETE':
          return 'SENDPARCEL';
        default:
          return 'WELCOME';
      }
    })();

    return <SetupProgress isXs={isXs} stages={stages} currentStage={cs} />;
  }, [isXs, screenToShow]);

  return (
    <OnboardingScreenWrapper progress={Progress}>
      {screenToShow === 'INTRO' && <IntroScreen isXs={isXs} onDone={() => setSetupStarted(true)} />}
      {screenToShow === 'PROFILE' && (
        <Profile
          isXs={isXs}
          onDone={(c) => {
            markAsDone(['SmartSendSetupCollectionAddressScreen', 'SmartSendActionCollectionAddressAdded'], [], [], {});
            setCustomer(c);
          }}
        />
      )}
      {screenToShow === 'RULES' && (
        <RulesScreen
          numberOfParcels={data.numberOfParcelsPerWeek}
          key={customer?.Id}
          customer={customer}
          isXs={isXs}
          onSkip={() => {
            markAsDone(['SmartSendSetupRuleScreen'], [], [], {});
          }}
          onDone={(added, meta) => {
            if (added > 0) {
              markAsDone(['SmartSendSetupRuleScreen', 'SmartSendActionRuleAdded'], [], [], meta);
            } else {
              markAsDone(['SmartSendSetupRuleScreen'], [], [], meta);
            }
          }}
        />
      )}
      {screenToShow === 'ACCOUNTS' && (
        <AccountsScreen
          isXs={isXs}
          justLinked={justLinked}
          justFailed={justFailed}
          signupIntegrations={data.signupIntegrations}
          onDone={(outstanding, done) => {
            // Record Integrations
            const achievements: AchievementType[] = ['SmartSendSetupAccountScreen'];
            if (!done?.length) {
              // Only onboarding account screen
            } else if (done?.some((x) => x === 'csv')) {
              achievements.push('SmartSendActionCsvUploaded');
            } else {
              achievements.push('SmartSendActionAccountLinked');
            }
            markAsDone(achievements, outstanding, done, {});
          }}
          onTodo={markAsTodo}
          clearJustFailed={() => setJustFailedAcknowledged(true)}
          clearJustLinked={() => setJustLinkedAcknowledged(true)}
        />
      )}
      {screenToShow === 'COMPLETE' && (
        <CompleteScreen
          isXs={isXs}
          actions={data.outstandingActions}
          done={done}
          justLinked={justLinked}
          onDone={sendNow}
        />
      )}
    </OnboardingScreenWrapper>
  );
};

export default SmartSendSetup;
